//
// Copyright 2026 DXOS.org
//
import * as Rpc from '@effect/rpc/Rpc';
import * as RpcGroup from '@effect/rpc/RpcGroup';
import * as Context from 'effect/Context';
import * as Schema from 'effect/Schema';
import { serviceError } from './service-rpc.js';
import { mutableArray } from './service-schemas.js';
//
// RPC message schemas.
//
/**
 * Feed query parameters.
 */
export const FeedQuery = Schema.Struct({
    spaceId: Schema.String,
    feedNamespace: Schema.optional(Schema.String),
    /**
     * Queries the whole space if missing.
     */
    feedIds: Schema.optional(mutableArray(Schema.String)),
    /**
     * Filter items after this cursor. Exclusive.
     */
    after: Schema.optional(Schema.String),
    /**
     * Filter items before this cursor. Exclusive.
     */
    before: Schema.optional(Schema.String),
    /**
     * Filter items after this position. Inclusive.
     * Proto `int64`, represented as a decimal string on the wire.
     */
    beginPosition: Schema.optional(Schema.String),
    /**
     * Filter items before this position. Exclusive.
     * Proto `int64`, represented as a decimal string on the wire.
     */
    endPosition: Schema.optional(Schema.String),
    limit: Schema.optional(Schema.Number),
    reverse: Schema.optional(Schema.Boolean),
    // TODO(dmaretskyi): Remove this field -- raw feeds dont index object IDs anymore.
    objectIds: Schema.optional(mutableArray(Schema.String)),
});
export const QueryFeedRequest = Schema.Struct({
    query: FeedQuery,
});
export const FeedQueryResult = Schema.Struct({
    /**
     * JSON-encoded object payloads. Each entry is a serialized ObjectJSON.
     * We use JSON strings instead of google.protobuf.Struct because Struct
     * coerces `undefined` to `null`, corrupting optional fields.
     */
    objects: Schema.optional(mutableArray(Schema.String)),
    /**
     * Cursor to query the next items. Can be passed to `after` in query to keep querying.
     */
    nextCursor: Schema.String,
    prevCursor: Schema.String,
});
export const InsertIntoFeedRequest = Schema.Struct({
    subspaceTag: Schema.String,
    spaceId: Schema.String,
    feedId: Schema.String,
    /**
     * JSON-encoded object payloads. Each entry is a serialized ObjectJSON.
     */
    objects: Schema.optional(mutableArray(Schema.String)),
});
export const DeleteFromFeedRequest = Schema.Struct({
    subspaceTag: Schema.String,
    spaceId: Schema.String,
    feedId: Schema.String,
    objectIds: Schema.optional(mutableArray(Schema.String)),
});
export const SyncFeedRequest = Schema.Struct({
    subspaceTag: Schema.String,
    spaceId: Schema.String,
    feedId: Schema.String,
    /**
     * Whether to push local changes to the server. Defaults to true.
     */
    shouldPush: Schema.optional(Schema.Boolean),
    /**
     * Whether to pull remote changes from the server. Defaults to true.
     */
    shouldPull: Schema.optional(Schema.Boolean),
});
export const GetSyncStateRequest = Schema.Struct({
    spaceId: Schema.String,
    /**
     * If empty, returns state for all namespaces synced by the client.
     */
    namespaces: Schema.optional(mutableArray(Schema.String)),
});
export const FeedNamespaceSyncState = Schema.Struct({
    namespace: Schema.String,
    /**
     * Blocks still to pull from remote. 0 when caught up.
     * Proto `int64`, represented as a decimal string on the wire.
     */
    blocksToPull: Schema.String,
    /**
     * Unpositioned blocks still to push to remote. 0 when caught up.
     * Proto `int64`, represented as a decimal string on the wire.
     */
    blocksToPush: Schema.String,
    /**
     * Total blocks stored locally for this namespace in the space.
     * Proto `int64`, represented as a decimal string on the wire.
     */
    totalBlocks: Schema.String,
});
export const GetSyncStateResponse = Schema.Struct({
    namespaces: Schema.optional(mutableArray(FeedNamespaceSyncState)),
});
/**
 * Effect RPC definitions for `dxos.client.services.FeedService`.
 * Service-only payloads use Effect schemas.
 */
export class Rpcs extends RpcGroup.make(Rpc.make('queryFeed', {
    payload: QueryFeedRequest,
    success: FeedQueryResult,
    error: serviceError,
}), Rpc.make('insertIntoFeed', {
    payload: InsertIntoFeedRequest,
    error: serviceError,
}), Rpc.make('deleteFromFeed', {
    payload: DeleteFromFeedRequest,
    error: serviceError,
}), Rpc.make('syncFeed', {
    payload: SyncFeedRequest,
    error: serviceError,
}), Rpc.make('getSyncState', {
    payload: GetSyncStateRequest,
    success: GetSyncStateResponse,
    error: serviceError,
})).prefix('FeedService.') {
}
/**
 * Effect service tag for the `FeedService` RPC handlers.
 */
export class Tag extends Context.Tag('@dxos/protocols/rpc/FeedService')() {
}
//# sourceMappingURL=FeedService.js.map